import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import RewardsService from "../../../services/reward";
import Utils from "../../../utility";
import { BusinessUser, BusinessConsent } from "../../../services";
import {
  widgetPreviewType,
  uiConstant,
  failureMessageConstant,
  successMessageConstant,
  paths,
  status,
  keyConstants
} from "../../../constants/index";
import ToggleSwitch from "../../../common/components/toggleSwitch";
import {
  hideLoader,
  removeUser,
  showLoader,
  updateUser
} from "../../../slices/userSlice";
import PopupWidget from "../../../common/components/widgetPreview/popupWidget";
import FooterWidget from "../../../common/components/widgetPreview/footerWidget";
import SideWidget from "../../../common/components/widgetPreview/sideWidget";
import history from "../../../managers/history";
import DropDown from "../../../common/components/dropdown";
import { Loader } from "../../../common/components/loader";
import PropTypes from "prop-types";
import AddCoupon from "../../dialog/addCoupon";
// import { partnerCouponMockData } from "../../../constants/mockData";
function SetupReward({ setRewardStatus, totalDiscount, setTotalDiscount }) {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();
  const [rewardId, setRewardId] = useState("");
  const [tierDiscounts, setTierDiscounts] = useState([]);
  const [tierConsents, setTierConsents] = useState([]);
  const [isConsentsEnabled, setIsConsentsEnabled] = useState(false);
  const [consentToggelErr, setConsentToggelErr] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [tierType, setTierType] = useState({ name: "", value: "" });
  const [showOption, setShowOption] = useState(false);
  const [currentDropDownIndex, setCurrentDropDownIndex] = useState(0);
  const [allTiers, setAllTiers] = useState([]);
  const [isOnlyEmailConsent, setIsOnlyEmailConsent] = useState(false);
  const [widgetSettings, setWidgetSettings] = useState({
    type: widgetPreviewType.DEFAULT,
    showAdWidget: false,
    adUnitCode: "",
    font: {
      family: "Montserrat",
      fileURL: ""
    },
    backgroundColor: "#0E0E0E",
    fontColor: "#FFFFFF",
    buttonColor: "#6C36B0"
  });
  const couponLimit = 10;
  const [couponSkip, setCouponSkip] = useState(0);
  const [showPopups, setShowPopups] = useState(false);
  const [couponList, setCouponList] = useState([]);
  const [totalNormalCoupon, setTotalNormalCoupon] = useState(0);
  const [totalPartnerCoupon, setTotalPartnerCoupon] = useState(0);
  const [highestTierDiscount, setHighestTierDiscount] = useState("");
  const businessId = user._id;

  const { IS_REFRESH_TOKEN_LOADING } = keyConstants;

  // Function to get the priority of a tier
  const getTierPriority = (tierName) => {
    switch (tierName) {
      case "Platinum Tier":
        return 3;
      case "Gold Tier":
        return 2;
      case "Silver Tier":
        return 1;
      default:
        return -1;
    }
  };

  const fetchNormalCoupons = async (businessId, skip, limit) => {
    if (skip > totalNormalCoupon) return [];
    try {
      const [error, response] = await Utils.parseResponse(
        new RewardsService().getCouponsByBusinessId(businessId, skip, limit)
      );

      if (error) {
        console.error("Coupon Error:", error);
        return [];
      }

      if (response?.data?.length > 0) {
        setTotalNormalCoupon(response?.total);
        return response.data
          .filter((item) => item?.status === status.ACTIVE)
          .map((item) => ({
            id: item?._id,
            discountValue: item?.discountValue
          }));
      }

      return [];
    } catch (error) {
      console.error("Error fetching coupons:", error);
      return [];
    }
  };

  const fetchPartnerCoupons = async (businessId, skip, limit) => {
    if (skip > totalPartnerCoupon) return [];
    try {
      const [errorPartnerCoupon, responsePartnerCoupon] =
        await Utils.parseResponse(
          new RewardsService().getPartnerCouponsByBusinessId(
            businessId,
            skip,
            limit
          )
        );

      if (errorPartnerCoupon) {
        console.error("Partner Coupon Error:", errorPartnerCoupon);
        return [];
      }

      if (responsePartnerCoupon?.partnerCoupons?.length > 0) {
        setTotalPartnerCoupon(responsePartnerCoupon?.totalPartnerCoupons);
        return responsePartnerCoupon.partnerCoupons
          .filter((item) => item?.status === status.ACTIVE)
          .map((item) => ({
            id: item?._id,
            discountValue: item?.name
          }));
      }

      return [];
    } catch (error) {
      console.error("Error fetching partner coupons:", error);
      return [];
    }
  };

  const fetchCouponData = async (skip, limit) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(fetchCouponData);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) {
        dispatch(showLoader());
        return;
      }
      dispatch(removeUser({}));
      return;
    }

    try {
      dispatch(showLoader());
      const businessId = user._id;

      // Fetch coupons in parallel
      const [normalCoupons, partnerCoupons] = await Promise.all([
        fetchNormalCoupons(businessId, skip, limit),
        fetchPartnerCoupons(businessId, skip, limit)
      ]);

      // Merge new coupons with existing ones
      const allCoupons = [
        ...(skip === 0 ? [] : couponList), // Include previous coupons from state
        ...normalCoupons,
        ...partnerCoupons
      ];

      setCouponList(allCoupons);
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const checkHigherPriorityViolation = (
    updatedTierDiscounts,
    currentTierPriority,
    discountValue,
    index
  ) => {
    const higherPriorityTiers = updatedTierDiscounts.filter((tier, i) => {
      return i !== index && getTierPriority(tier.name) > currentTierPriority;
    });

    for (const tier of higherPriorityTiers) {
      if (
        !Number.isNaN(tier.discount) &&
        Utils.isPurelyNumeric(tier.discount) &&
        parseFloat(tier.discount) < parseFloat(discountValue)
      ) {
        const higherTiersNames = higherPriorityTiers
          .map((tier) => tier.name.split(" ")[0])
          .join(" and ");
        const tierOrTiers = higherPriorityTiers.length === 1 ? "Tier" : "Tiers";
        Utils.failureToastMessage(
          `${updatedTierDiscounts[index].name} must have a lower discount value than ${higherTiersNames} ${tierOrTiers} due to its lower priority.`
        );
        return true;
      }
    }

    return false;
  };

  const checkLowerPriorityViolation = (
    updatedTierDiscounts,
    currentTierPriority,
    discountValue,
    index
  ) => {
    const lowerPriorityTiers = updatedTierDiscounts.filter((tier, i) => {
      return i !== index && getTierPriority(tier.name) < currentTierPriority;
    });

    for (const tier of lowerPriorityTiers) {
      if (
        !Number.isNaN(tier.discount) &&
        Utils.isPurelyNumeric(tier.discount) &&
        parseFloat(tier.discount) > parseFloat(discountValue)
      ) {
        const lowerTiersNames = lowerPriorityTiers
          .map((tier) => tier.name.split(" ")[0])
          .sort()
          .join(" and ");
        const tierOrTiers = lowerPriorityTiers.length === 1 ? "Tier" : "Tiers";
        Utils.failureToastMessage(
          `${updatedTierDiscounts[index].name} must have a higher discount value than ${lowerTiersNames} ${tierOrTiers} due to its higher priority.`
        );
        return true;
      }
    }

    return false;
  };

  const fetchBusinessDetails = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(fetchBusinessDetails);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    try {
      const businessDetails = await new BusinessUser().getBusiness(businessId);
      if (
        businessDetails &&
        businessDetails.settings &&
        businessDetails.settings.widgetSettings
      ) {
        const {
          type,
          theme,
          font,
          showAdWidget,
          adUnitCode,
          isEmailMarketingEnabled
        } = businessDetails.settings.widgetSettings;
        setWidgetSettings({
          type: type,
          showAdWidget: showAdWidget,
          adUnitCode: adUnitCode,
          backgroundColor: theme?.backgroundColor,
          fontColor: theme?.fontColor,
          buttonColor: theme?.buttonColor,
          font: font,
          isEmailMarketingEnabled: isEmailMarketingEnabled
        });
        setAllTiers(businessDetails?.tiers);
      }
    } catch (error) {
      Utils.failureToastMessage(error?.message);
    }
  };

  const getConsentListByBusinessID = async () => {
    const [error, responseConsents] = await Utils.parseResponse(
      new BusinessConsent().getConsentList(businessId)
    );
    if (error) {
      console.error(error);
      return;
    }
    if (
      responseConsents &&
      responseConsents.consentsList &&
      responseConsents.consentsList.length > 0
    ) {
      const updatedConsents = responseConsents.consentsList.flatMap((item) =>
        item.consents
          .filter((consent) => consent.isActive)
          .map((element) => ({
            ...element,
            tierType: {
              name: item.tierType,
              value: element._id
            }
          }))
      );
      if (
        updatedConsents.length === 1 &&
        updatedConsents[0].name === "E-Mail Marketing Consent"
      ) {
        setIsOnlyEmailConsent(true);
      }
      setTierConsents(updatedConsents);
      const updatedTierDiscounts = responseConsents.consentsList.map(
        (item) => ({
          name: item.tierType,
          discount: item.discount
        })
      );
      setTierDiscounts(updatedTierDiscounts);
      const highestTierDiscount =
        Utils.getHighestTierDiscount(updatedTierDiscounts);
      setHighestTierDiscount(highestTierDiscount);
    }
  };

  useEffect(() => {
    if (!tierType?.name) return;

    const getUpdatedTierType = (item, index) => {
      return {
        name:
          currentDropDownIndex === index ? tierType.name : item.tierType.name,
        value: allTiers.every((tier) => tier.value)
          ? item.tierType.value
          : tierType.value
      };
    };

    const getUpdatedTierId = (name) => {
      const matchingTier = allTiers.find((tier) => tier.name === name);
      return matchingTier ? matchingTier._id : null;
    };

    const getTierDiscountValue = (matchingItem, name) => {
      if (matchingItem && matchingItem.tierType.value) {
        return matchingItem.tierType.value;
      } else {
        const matchingTier = allTiers.find((tier) => tier.name === name);
        return matchingTier ? matchingTier.discountValue : "";
      }
    };

    const updateTierConsents = () => {
      return tierConsents
        .filter((consent) => consent.isActive)
        .map((item, index) => {
          const updatedTierType = getUpdatedTierType(item, index);
          const updatedTierId = getUpdatedTierId(updatedTierType.name);

          return {
            ...item,
            tierType: updatedTierType,
            tier: {
              _id: updatedTierId
            }
          };
        });
    };

    const extractUniqueTiers = (updatedtier) => {
      const uniqueTierNames = Array.from(
        new Set(updatedtier.map((item) => item.tierType.name))
      );

      return uniqueTierNames.map((name) => {
        const matchingItem = updatedtier.find(
          (item) => item.tierType.name === name
        );
        const value = getTierDiscountValue(matchingItem, name);

        return {
          name: name,
          discount: value
        };
      });
    };

    const updatedtier = updateTierConsents();
    setTierType({ name: "", value: "" });
    setTierConsents(updatedtier);

    const uniqueTiers = extractUniqueTiers(updatedtier);
    const discountValues = uniqueTiers.map((item) => item.discount);
    setTotalDiscount(Utils.formatDiscounts(discountValues));
    setTierDiscounts(uniqueTiers);
    const highestTierDiscount = Utils.getHighestTierDiscount(uniqueTiers);
    setHighestTierDiscount(highestTierDiscount);
  }, [currentDropDownIndex, tierType, tierConsents, allTiers]);

  const getAllRewards = async () => {
    const tokenStatus = await Utils.checkTokenExpireOrNot(getAllRewards);
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) {
        dispatch(showLoader());
        return;
      }
      dispatch(removeUser({}));
      return;
    }
    try {
      dispatch(showLoader());
      let [, getRewardRes] = await Utils.parseResponse(
        new RewardsService().getRewardByBusinessId(businessId)
      );

      if (getRewardRes && Object.keys(getRewardRes).length > 0) {
        const { isConsentsEnabled, tiers, _id } = getRewardRes;

        setRewardId(_id);
        if (_id) {
          dispatch(
            updateUser({
              ...user,
              rewardId: _id,
              rewardStatus: isConsentsEnabled
            })
          );
        } else {
          setRewardStatus(false);
        }
        setIsConsentsEnabled(isConsentsEnabled);
        const discountValues = tiers.map((tier) => tier.tier.discountValue);
        setTotalDiscount(Utils.formatDiscounts(discountValues));

        const updatedConsents = tiers.flatMap((item) =>
          item.consents
            .filter((consent) => consent.isActive)
            .map((element) => ({
              ...element,
              tierType: {
                name: item.tier.name,
                value: element._id
              }
            }))
        );
        if (
          updatedConsents.length === 1 &&
          updatedConsents[0].name === "E-Mail Marketing Consent"
        ) {
          setIsOnlyEmailConsent(true);
        }
        setTierConsents(updatedConsents);

        const updatedTierDiscounts = tiers.map((item) => ({
          name: item.tier.name,
          discount: item.tier.discountValue
        }));
        setTierDiscounts(updatedTierDiscounts);
        const highestTierDiscount =
          Utils.getHighestTierDiscount(updatedTierDiscounts);
        setHighestTierDiscount(highestTierDiscount);
      } else {
        getConsentListByBusinessID();
      }
    } catch (error) {
      Utils.failureToastMessage(error?.message);
    } finally {
      setIsLoading(false);
      dispatch(hideLoader());
    }
  };

  const handleRewardOperation = async (rewardId, reqObj) => {
    const tokenStatus = await Utils.checkTokenExpireOrNot();
    if (tokenStatus) {
      if (tokenStatus === IS_REFRESH_TOKEN_LOADING) return;
      dispatch(removeUser({}));
      return;
    }
    const rewardsService = new RewardsService();
    const [error] = await Utils.parseResponse(
      rewardId
        ? rewardsService.updateReward(reqObj)
        : rewardsService.addReward(reqObj)
    );

    if (error) {
      if (error?.response?.data?.message) {
        if (
          error?.response?.data?.message ===
          "Cannot turn on rewards since there are no valid coupons"
        ) {
          throw new Error("Add coupon inventory for all discounts");
        } else {
          throw new Error(error?.response?.data?.message);
        }
      } else {
        throw new Error(error.message);
      }
    }

    return rewardId
      ? successMessageConstant.REWARD_UPDATED
      : successMessageConstant.REWARD_ADDED;
  };

  const getModifiedConsentsTiers = (tierConsents) => {
    return tierConsents.map((item) => {
      return {
        consentId: item?._id,
        tierId: item?.tier?._id || item?.tier
      };
    });
  };

  const getModifiedTierDiscounts = (tierConsents, tierDiscounts) => {
    return tierConsents
      .flatMap((tierConsent) => {
        return tierDiscounts
          .map((tierDiscount) => {
            if (
              tierConsent?.tier?.name === tierDiscount?.name ||
              tierConsent?.tierType?.name === tierDiscount?.name
            ) {
              return {
                tierId: tierConsent?.tier?._id || tierConsent?.tier,
                discountValue: tierDiscount?.discount.toString()
              };
            }
          })
          .filter(Boolean);
      })
      .filter((obj, index, self) => {
        return (
          index ===
          self.findIndex(
            (t) =>
              t &&
              t.tierId === obj.tierId &&
              t.discountValue === obj.discountValue.toString()
          )
        );
      });
  };

  const createRequestObject = (
    value,
    modifiedConsentsTiers,
    modifiedTierDiscounts
  ) => {
    (modifiedConsentsTiers = modifiedConsentsTiers.filter(
      (consent) => consent?.tierId && consent?.tierId?._id !== null
    )),
      (modifiedTierDiscounts = modifiedTierDiscounts.filter(
        (tier) => tier?.tierId && tier?.tierId?._id !== null
      ));
    return {
      isEmailMarketingEnabled: false,
      emailMarketingDiscount: 0,
      isConsentsEnabled:
        modifiedConsentsTiers.length > 0
          ? value === "toggel"
            ? !isConsentsEnabled
            : isConsentsEnabled
          : false,
      consentTiers: modifiedConsentsTiers,
      tierDiscounts: modifiedTierDiscounts,
      user: user,
      businessId: user?._id
    };
  };

  const handleError = (error) => {
    if (error?.message) {
      setConsentToggelErr(
        error?.message ===
          failureMessageConstant.ADD_COUPON_INVENTORY_FOR_ALL_DISCOUNT
          ? failureMessageConstant.CLICK_HERE_TO_ADD_COUPON
          : error?.message
      );
      setIsConsentsEnabled(false);
    }
  };

  /**
   * Adds or updates consent discounts.
   */
  const addOrUpdateConsentDiscounts = async (value) => {
    dispatch(showLoader());

    try {
      const modifiedConsentsTiers = getModifiedConsentsTiers(tierConsents);
      const modifiedTierDiscounts = getModifiedTierDiscounts(
        tierConsents,
        tierDiscounts
      );
      const reqObj = createRequestObject(
        value,
        modifiedConsentsTiers,
        modifiedTierDiscounts
      );

      const successMessage = await handleRewardOperation(rewardId, reqObj);
      Utils.successToastMessage(successMessage);
      await getAllRewards();
    } catch (error) {
      handleError(error);
    } finally {
      dispatch(hideLoader());
    }
  };

  const handleDiscountSwitch = async () => {
    if (!isConsentsEnabled && !totalDiscount) {
      setConsentToggelErr(
        failureMessageConstant.PLEASE_ENSURE_REWARD_IS_ASSIGNED_TO_TIER
      );
      return;
    }
    setIsConsentsEnabled(!isConsentsEnabled);
    await addOrUpdateConsentDiscounts("toggel");
    if (consentToggelErr) {
      setConsentToggelErr("");
    }
  };

  // Function to validate discounts
  const checkValidDiscount = (discounts) => {
    const tiers = discounts.filter((discount) => discount.name !== "No Tier");
    if (tiers.length === 0) {
      return false;
    }
    const uniqueDiscounts = new Set();
    return tiers.every((discount) => {
      const discountValue = String(discount.discount).trim();
      if (Utils.isPurelyNumeric(discountValue)) {
        const numDiscount = Number(discountValue);
        if (
          numDiscount <= 1 ||
          numDiscount >= 100 ||
          uniqueDiscounts.has(numDiscount)
        ) {
          return false;
        }
        uniqueDiscounts.add(numDiscount);
      } else {
        if (discountValue.length === 0 || uniqueDiscounts.has(discountValue)) {
          return false;
        }
        uniqueDiscounts.add(discountValue);
      }
      return true;
    });
  };

  /**
   * Add coupon inventory.
   *
   * @return {Promise<void>} A promise that resolves when the coupon inventory is added.
   */

  useEffect(() => {
    getAllRewards();
    fetchBusinessDetails();
    fetchCouponData(0, couponLimit);
  }, []);

  if (isLoading) {
    return;
  }

  const getPreviewWidget = () => {
    switch (widgetSettings.type) {
      case widgetPreviewType.FOOTER:
        return (
          <FooterWidget
            isOnlyEmailConsent={isOnlyEmailConsent}
            isConsentsEnabled={isConsentsEnabled}
            discount={highestTierDiscount}
            widgetSettings={widgetSettings}
          />
        );
      case widgetPreviewType.POPUP:
        return (
          <PopupWidget
            isOnlyEmailConsent={isOnlyEmailConsent}
            isConsentsEnabled={isConsentsEnabled}
            discount={highestTierDiscount}
            widgetSettings={widgetSettings}
          />
        );
      case widgetPreviewType.SIDE:
        return (
          <SideWidget
            isOnlyEmailConsent={isOnlyEmailConsent}
            isConsentsEnabled={isConsentsEnabled}
            discount={highestTierDiscount}
            widgetSettings={widgetSettings}
          />
        );
      default:
        return (
          <>
            <Loader />
          </>
        );
    }
  };

  const handleDropdownDiscountChange = (newValue, index) => {
    const selectedDiscount = newValue.name;

    // Create a copy of the current tier discounts and filter out "No Tier"
    const updatedTierDiscounts = [...tierDiscounts]
      .filter((content) => content.name !== "No Tier") // Exclude "No Tier"
      .sort((a, b) => {
        const sortOrder = ["Silver Tier", "Gold Tier", "Platinum Tier"];
        return sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name);
      });

    // Check if the selected discount is a numeric value
    if (!isNaN(parseFloat(selectedDiscount)) && isFinite(selectedDiscount)) {
      const numericDiscount = parseFloat(selectedDiscount);

      // Validate if it's a number between 0 and 100
      if (numericDiscount <= 100 && numericDiscount >= 0) {
        // Get the current tier's priority
        const currentTierPriority = getTierPriority(
          updatedTierDiscounts[index].name
        );

        // Check for priority violations if it's a number
        if (currentTierPriority !== -1) {
          if (
            checkHigherPriorityViolation(
              updatedTierDiscounts,
              currentTierPriority,
              numericDiscount,
              index
            ) ||
            checkLowerPriorityViolation(
              updatedTierDiscounts,
              currentTierPriority,
              numericDiscount,
              index
            )
          ) {
            return; // Exit if there are violations
          }
        }

        // If validation passes, assign the discount
        updatedTierDiscounts[index].discount = numericDiscount;
      } else {
        Utils.failureToastMessage(
          "Numeric discount must be between 0 and 100."
        );
      }
    } else {
      // If the selected discount is a string, assign it directly
      updatedTierDiscounts[index].discount = selectedDiscount;
    }
    // Update the state with the sorted discounts
    setTierDiscounts(updatedTierDiscounts);
    const highestTierDiscount =
      Utils.getHighestTierDiscount(updatedTierDiscounts);
    setHighestTierDiscount(highestTierDiscount);
  };

  return (
    <div className="mb-20 mt-10 ">
      <div className="flex flex-col gap-41px 2xl:flex-row">
        <div className="w-full lg:min-w-521px 2xl:w-1/2">
          <div className="mt-5">
            <div className="flex flex-row items-center gap-2">
              <div className="font-MontserratSemiBold text-ft16-19.5 text-black-000000">
                Offer Discounts for Consents
              </div>
              <ToggleSwitch
                toggleSwitch={isConsentsEnabled}
                handleSwitch={handleDiscountSwitch}
              />
            </div>
            {consentToggelErr && (
              <div
                onClick={() => history.push(paths.COUPON_INVENTORY)}
                className={`relative mt-2 flex gap-1.5 rounded border border-red-10 bg-red-20 px-4 py-3 text-red-10 ${
                  consentToggelErr ==
                  failureMessageConstant.CLICK_HERE_TO_ADD_COUPON
                    ? "cursor-pointer"
                    : "pointer-events-none"
                }`}
                role="alert"
              >
                <img src="/images/info-circle.svg" alt="info" />
                <span className="block sm:inline">
                  <span className="font-bold">Error: </span>
                  {consentToggelErr}
                </span>
              </div>
            )}
            <div>
              <div className="mt-25px flex font-MontserratMedium text-ft16-24">
                <span className="mr-1 flex size-5.5 justify-center rounded-full bg-purple-D0B4FF text-white">
                  1
                </span>
                <span className="text-black-30">Assign Consent to Tiers</span>
              </div>
              <div className="rounded bg-purple-FCF7FF">
                {tierConsents &&
                  tierConsents?.length > 0 &&
                  tierConsents.map((content, index) => (
                    <div
                      key={content?._id}
                      className={`flex h-auto w-full flex-col items-start justify-between ${tierConsents?.length - 1 === index ? "" : "border-b border-grey-E3E3E3"} px-3 pb-3 pt-2 md:h-17 md:flex-row md:items-center md:py-2.5 md:pl-6 md:pr-5.5`}
                    >
                      <div>
                        <p className="font-MontserratMedium text-ft15-26.25 text-black-30">
                          {content?.name}
                        </p>
                      </div>
                      <DropDown
                        index={index}
                        value={
                          content.tierType.name === "No Tier"
                            ? { name: "Select Tier", value: "" }
                            : content.tierType
                        }
                        setValue={setTierType}
                        data={uiConstant.tierData}
                        placeHolder={"Select Tier"}
                        customizeDropDown={{
                          maxWidth: "md:max-w-41.5",
                          height: "h-49px",
                          contentHeight: "h-49px"
                        }}
                        showOption={showOption}
                        setShowOption={setShowOption}
                        currentDropDownIndex={currentDropDownIndex}
                        setCurrentDropDownIndex={setCurrentDropDownIndex}
                      />
                    </div>
                  ))}
              </div>
            </div>
            <div className="mb-19px mt-25px flex font-MontserratMedium text-ft16-24">
              <span className="mr-1 flex size-5.5 justify-center rounded-full bg-purple-D0B4FF text-white">
                2
              </span>
              <span className="text-black-30">Set Discounts</span>
            </div>
            <div>
              {tierDiscounts &&
                tierDiscounts.length > 0 &&
                tierDiscounts
                  .filter((content) => content.name !== "No Tier")
                  .sort((a, b) => {
                    const sortOrder = [
                      "Silver Tier",
                      "Gold Tier",
                      "Platinum Tier"
                    ];
                    const indexA = sortOrder.indexOf(a.name);
                    const indexB = sortOrder.indexOf(b.name);
                    return indexA - indexB;
                  })
                  .map((content, index) => (
                    <div
                      key={content.name.toLowerCase()}
                      className="mt-3 flex h-auto w-full flex-col items-start justify-between rounded bg-purple-FCF7FF px-3 pb-3 pt-2 md:flex-row md:items-center md:py-2.5 md:pl-6 md:pr-5.5"
                    >
                      <p className="font-MontserratMedium text-ft15-26.25 text-black-30">
                        {content.name}
                      </p>
                      <DropDown
                        index={index + 100}
                        value={
                          content.discount
                            ? { name: content.discount?.toString() || "" }
                            : { name: "Select Reward", value: "" }
                        }
                        setValue={(newValue) =>
                          handleDropdownDiscountChange(newValue, index)
                        }
                        data={couponList.map((item) => ({
                          name: item.discountValue.toString(),
                          value: item._id
                        }))}
                        placeHolder="Select Reward"
                        customizeDropDown={{
                          maxWidth: "md:max-w-41.5",
                          height: "h-49px",
                          contentHeight: "h-49px"
                        }}
                        showOption={showOption}
                        setShowOption={setShowOption}
                        currentDropDownIndex={currentDropDownIndex}
                        setCurrentDropDownIndex={setCurrentDropDownIndex}
                        textwidth="w-31"
                        buttonText="Add Coupon"
                        buttonIcon="/images/plus-purple.svg"
                        buttonOnClick={() => {
                          setShowPopups(!showPopups), setShowOption(false);
                        }}
                        onScrollToEnd={async () => {
                          let skip = couponSkip + couponLimit;
                          setCouponSkip(skip);
                          fetchCouponData(skip, couponLimit);
                        }}
                        isLoading={user?.loading}
                      />
                    </div>
                  ))}
            </div>
          </div>
          <hr className="mt-6.5 border-grey-E3E3E3" />
          <div className="flex justify-end">
            <button
              className={`mt-10.5 h-12 w-45 rounded ${
                checkValidDiscount(tierDiscounts)
                  ? "bg-purple-6C36B0"
                  : "pointer-events-none bg-grey-B0B1B2"
              } font-MontserratMedium text-ft15 text-white`}
              onClick={addOrUpdateConsentDiscounts}
            >
              {!rewardId ? "Save Rewards" : "Update Rewards"}
            </button>
          </div>
        </div>
        <div className="flex w-full flex-col 2xl:w-1/2">
          {widgetSettings?.font?.family && widgetSettings?.font?.fileURL && (
            <style>
              {`
          @font-face {
            font-family: '${widgetSettings?.font?.family}';
            src: url('${widgetSettings?.font?.fileURL}');
          }
        `}
            </style>
          )}
          {!showPopups && (
            <p className="mb-13px whitespace-nowrap text-ft16-19.5 font-semibold ">
              Consent Widget Preview
            </p>
          )}
          <div className="w-full overflow-auto">{getPreviewWidget()}</div>
        </div>
      </div>
      {showPopups && (
        <AddCoupon
          handleClose={() => setShowPopups(!showPopups)}
          fetchCouponData={() => fetchCouponData(0, couponLimit)}
        />
      )}
    </div>
  );
}
SetupReward.propTypes = {
  setRewardStatus: PropTypes.func,
  totalDiscount: PropTypes.number,
  setTotalDiscount: PropTypes.func
};

export default SetupReward;
